import { BallType } from 'lib_ts/enums/machine.enums';
import { IPitch } from 'lib_ts/interfaces/pitches';
import { useEffect, useRef } from 'react';
import { StateTransformService } from 'services/state-transform.service';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';

const HOOK_NAME = 'useDebugPitchAutoTms';

// creates TMS records for debug pitches whenever necessary
export const useDebugPitchAutoTms = (props: {
  machineID: string;
  ballType: BallType;
  pitches: IPitch[];
  force?: boolean;
}) => {
  const loading = useTrainedMsStore((s) => s.loading);

  // key: pitch._id, value: true if the pitch has been forced
  const forced = useRef<Record<string, boolean>>({});

  // MARK: insert TMS records for any debug pitches found w/o TMS records
  useEffect(() => {
    if (loading) {
      return;
    }

    const callback = async () => {
      for (const pitch of props.pitches) {
        if (!pitch.debugMs) {
          // not a debug pitch, skip
          continue;
        }

        const shouldForce = props.force && !forced.current[pitch._id];

        if (
          pitch._mx_tms && // already has a TMS record
          !shouldForce // already forced this pitch 1x
        ) {
          // already has a TMS record, skip
          continue;
        }

        if (pitch._mx_tms) {
          // if we're proceeding when _mx_tms is present, then it's by force
          forced.current[pitch._id] = true;
        }

        const buildResult = await StateTransformService.getInstance().buildMs({
          machineID: props.machineID,
          ms: pitch.debugMs,
        });

        if (!buildResult) {
          // failed to build TMS record, skip
          console.error(
            `${HOOK_NAME}: failed to build TMS record for debug pitch ${pitch.name} (${pitch._id})`
          );
          continue;
        }

        console.debug(
          `${HOOK_NAME}: built TMS record for debug pitch ${pitch.name} (${pitch._id})`,
          buildResult
        );

        const saveResult = await useTrainedMsStore.getState().upsertTms({
          machineID: props.machineID,
          ball_type: props.ballType,
          bs_hash: pitch.bs_hash,
          aimingTraj: buildResult.traj,
          ms: buildResult.ms, // ? could be clamped or otherwise modified to fit this specific machine x ball type x model
          shotIDs: [],
          deleted: false,
          debug: true,
        });

        console.debug(
          `${HOOK_NAME}: saved TMS record for debug pitch ${pitch.name} (${pitch._id})`,
          saveResult
        );
      }
    };

    const timeout = setTimeout(() => {
      callback();
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [loading, forced, props.pitches, props.machineID, props.ballType]);
};
